import { useActiveOrganizationId } from "@/contexts"
import { ApiQueryName } from "@/models/api"
import { useQueryKey } from "@/services/utils"
import { httpClient, toHttpError } from "@/utils/api"
import { useQuery } from "@tanstack/react-query"

import type { BillDocumentResponse } from "../pages/variance-route/variance.types"

// The shared `httpClient` response interceptor (`initCaseResponseInterceptor`
// in `@/utils/interceptors`) camelCases every JSON response body *except* URLs
// that contain `organizations/…/bills` or are explicitly allow-listed. This
// route is `/api/v1/neat/:organization_id/bills/:bill_id/document` (no
// `organizations/` segment), so it does NOT match that passthrough and the
// wire's `download_url` arrives here as `downloadUrl` (`kind`/`content` are
// single words, transform-invariant). Map `downloadUrl` back so this hook's
// public contract stays snake_case, matching the BFF response shape.
interface RawBillDocumentResponse {
  kind: BillDocumentResponse["kind"]
  downloadUrl?: string
  content?: string | null
}

// This BFF-only route isn't part of insights-api's OpenAPI spec, so it's
// absent from the generated `insights-api-schema.d.ts` and can't go through
// the typed `client()`. Fall back to the shared axios `httpClient`, matching
// `use-bill.ts` / `use-bill-document-preview.ts`.
const getBillDocument = async (
  organizationId: number,
  billId: number
): Promise<BillDocumentResponse> => {
  try {
    const response = await httpClient.get<RawBillDocumentResponse>(
      `/api/v1/neat/${organizationId}/bills/${billId}/document`
    )
    const { kind, downloadUrl, content } = response.data
    const result: BillDocumentResponse = { kind }
    if (downloadUrl) {
      result.download_url = downloadUrl
    }
    if (content != null) {
      result.content = content
    }
    return result
  } catch (error: unknown) {
    throw toHttpError(error)
  }
}

/**
 * Lazily resolves an anomalous month's candidate bill document
 * (`{ kind: "pdf" | "json" | "none", download_url? (pdf), content? (json) }`).
 * The JSON source is proxied inline by the BFF (`content`) because a browser
 * fetch to the presigned S3 URL is CORS-blocked. A no-op — no fetch — when
 * `billId` is null (the metered-only case: the matcher found no candidate
 * bill for that month, so there's nothing to classify).
 */
export const useGetBillDocument = (billId: number | null) => {
  const { activeOrganizationId } = useActiveOrganizationId()
  const queryKey = useQueryKey(
    ApiQueryName.BillDocument,
    "getOne",
    billId != null ? String(billId) : null,
    { endpoint: "document" }
  )

  return useQuery<BillDocumentResponse>({
    queryKey,
    enabled: billId != null && activeOrganizationId != null,
    queryFn: () =>
      getBillDocument(Number(activeOrganizationId), billId as number),
  })
}
